/**
 * bpmnLayout - Auto-organização vertical do mapa (cima → baixo)
 *
 * Usa o dagre para calcular a ordem topológica do processo no sentido
 * vertical, com os ramos de uma decisão posicionados lado a lado logo abaixo
 * do bloco de origem. Cada símbolo entra no cálculo com a sua dimensão real
 * (evento ≠ tarefa ≠ gateway), evitando blocos colados e linhas cruzadas.
 *
 * @version 3.0.0
 * RELEASE: 2026-08-31 v3.0.0 - Dimensões reais por símbolo e espaçamento maior
 *          para leitura clara do processo.
 * RELEASE: 2026-08-31 v2.0.0 - Layout vertical (rankdir TB) e remoção das raias.
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (designer BPMN)
 */

import dagre from 'dagre';
import type { WorkflowEditorEdge, WorkflowEditorNode } from '@/hooks/useWorkflowDesigner';
import { dimensaoDaForma, formaDoRegistro } from './bpmnShapes';

export interface PosicaoCalculada {
  id: string;
  x: number;
  y: number;
}

/**
 * Calcula as posições de todos os blocos no sentido vertical.
 * Arestas de retrocesso são ignoradas na ordenação para não puxarem o fluxo
 * principal para cima e criarem cruzamentos desnecessários.
 */
export function calcularLayoutBpmn(
  nodes: WorkflowEditorNode[],
  edges: WorkflowEditorEdge[],
): PosicaoCalculada[] {
  if (nodes.length === 0) return [];

  const grafo = new dagre.graphlib.Graph();
  grafo.setDefaultEdgeLabel(() => ({}));
  grafo.setGraph({
    rankdir: 'TB',
    // v3.1.0: mais respiro entre ramos e ordenação com menos cruzamentos
    nodesep: 170,
    ranksep: 210,
    edgesep: 60,
    marginx: 100,
    marginy: 100,
    ranker: 'network-simplex',
  });


  // Dimensão real de cada símbolo evita sobreposição no resultado do dagre
  const dimensoes = new Map<string, { largura: number; altura: number }>();
  nodes.forEach((n) => {
    const dim = dimensaoDaForma(formaDoRegistro(n));
    dimensoes.set(n.id, dim);
    grafo.setNode(n.id, { width: dim.largura, height: dim.altura });
  });

  edges.forEach((e) => {
    if (e.is_regression) return;
    if (grafo.hasNode(e.from_node_id) && grafo.hasNode(e.to_node_id)) {
      grafo.setEdge(e.from_node_id, e.to_node_id);
    }
  });

  dagre.layout(grafo);

  return nodes.map((n) => {
    const info = grafo.node(n.id);
    const dim = dimensoes.get(n.id) ?? { largura: 300, altura: 170 };
    return {
      id: n.id,
      x: Math.round((info?.x ?? 0) - dim.largura / 2),
      y: Math.round((info?.y ?? 0) - dim.altura / 2),
    };
  });
}
